/*
 * Filename: SfConnector.ts
 * Description:Connects our app to salesforce
 */

import { CQAppConstant } from 'app-constants';
import { SFPackageVersion } from 'sf-app/sf-package';
import Cookies from 'universal-cookie';
import axios from 'axios';


const jsforce : any = require('jsforce');

export interface IAnonymousResult {
  success : boolean;
  error?: any
}
export interface User {
  username: string;
  email: string;
  photoUrl: string;
  profileName: string;
  fullName: string;
  orgId?: string;
  instanceURL? : string;
}

export class InstallationEvent {
  progress ?: number;
  isSuccessFul ?: boolean;
  isFailed ?: boolean;
  isInProgress ?: boolean;
}

export interface ISubscriberPackage {
  Name: string;
  IsPackageValid: string;
  NamespacePrefix: string;
}

export interface ISubscriberPackageVersion {
  MajorVersion: number;
  MinorVersion: number;
  PatchVersion: number;
  Name?: string;
}
export interface IInstalledPackageVersion {
  SubscriberPackageId: string;
  SubscriberPackageVersionId: string;
  SubscriberPackage: ISubscriberPackage;
  SubscriberPackageVersion: ISubscriberPackageVersion;
}

export interface IOrgData {
  user: User;
  orgName: string;
  isSandbox: boolean;
}

export interface IProfile {
  Id: string;
  Name: string;
  FullName: string;
}

export interface IProfileMapping {
  target: String;
  source: String;
}

export interface IQueryResult {
  done: boolean;
  totalSize: number;
  records: any[];
}
export interface DataTypeProfile {
  type: string;
}


export class SfConnector {
  private readonly cookie: Cookies;
  private readonly jsForce: any;
  private jsForceConnection: any;
  constructor() {
    this.cookie = new Cookies();
    this.jsForce = jsforce;
  }
  private readonly pollingTimeout = 1800 * 1000 ; // 30 minutes timeout
  public static releaseVersionName: string = '';

  /**
   * Returns the url of the proxy server based on location. If localhost:8080 is used returns localhost:8088 else
   * returns own location
   */
  private getProxyURL() {
    let currentLocation = window.location;
    if(currentLocation.host !== CQAppConstant.LOCAL_HOST_NAME) {
      return `${currentLocation.protocol}//${currentLocation.hostname}:${currentLocation.port}`;
    }

    return CQAppConstant.LOCAL_SERVER;
  }

  private instanceUrl = '';
  
  /**
   * Checks the authentication status by sending a GET request to the
   * CHECK_AUTH endpoint.
   *
   * @async
   * @function checkAuth
   * Updates the instance URL from the response upon successful authentication.
   * @returns {Promise<import('axios').AxiosResponse|null>}  
   *          Resolves with the Axios response if the request succeeds,  
   *          or `null` if an error occurs.
   *
   * @throws Does not throw; errors are caught internally and logged.
 */
  public async checkAuth () {
    try {
      const res = await this.getAxiosInstance().get(CQAppConstant.CHECK_AUTH);
      this.instanceUrl = res.data.instanceUrl;
      return res;
    } catch (error) {
      return null;
    }
  };

  /**
   * Checks if the current Salesforce session is still valid.
   * 
   * @async
   * @function isSessionValid
   * @returns {Promise<boolean>} True if session is valid, false otherwise
   */
  public async isSessionValid(): Promise<boolean> {
    try {
      const res = await this.checkAuth();
      return res !== null && res.status === 200;
    } catch {
      return false;
    }
  }


  /**
   * Logs the user out by clearing session cookies, initiating jsForce logout,
   * and resolving once the connection is fully disconnected.
   *
   * @returns {Promise<void>} A promise that resolves when the logout process
   *          completes and the jsForce connection is disconnected.
   */
  async logOut() {
    try {
      await this.getAxiosInstance().post(CQAppConstant.LOGOUT);
    } finally {
      this.jsForceConnection = null;
      this.cachedOrgData = null;
      this.installedPackageCache = undefined;
    }
  }


  private installedPackageCache : IInstalledPackageVersion[] | undefined = undefined; //List of installed packages before installation from CQ Deployment Agent

  /**
   * Retrieves the installed package version(s) for the given `PackageId` by querying Salesforce Tooling API.
   * The result is cached in `installedPackageCache`.
   *
   * @param {boolean} latest - Determines whether to query or return from cache.
   * @returns {Promise<IInstalledPackageVersion[]>} A promise that resolves to an array of installed package versions.
   * @throws {Error} If the query to Salesforce's tooling API fails, the promise is rejected with an error.
   */
  public getInstalledPackages(latest = false) : Promise<IInstalledPackageVersion[]>{
    if(this.installedPackageCache && !latest) {
      return Promise.resolve(this.installedPackageCache);
    }

    return new Promise<any>((resolve, reject) => {
      this.fetchInstalledPackagesInfo()
        .then((records: any) => {
          const packages = JSON.parse(JSON.stringify(records));
          if (!latest) {
            this.installedPackageCache = packages;
          }
          SfConnector.releaseVersionName = packages.find((pkg: IInstalledPackageVersion) => pkg.SubscriberPackage.Name === CQAppConstant.COMPLIANCEQUEST)?.SubscriberPackageVersion?.Name;
          resolve(packages);
        })
        .catch((err: any) => {
          reject(err);
        });
      });
  }

    /**
   * Fetches the package version name from the Salesforce proxy endpoint.
   *
   * Sends a GET request to the `/packageVersion` endpoint using common Salesforce headers.
   * Returns the package version name as received from the response data.
   */
  async fetchInstalledPackagesInfo() {
    try {
      const response = await SFConnection.executeToolingQuery(CQAppConstant.INSTALLED_PACKAGES, [CQAppConstant.ORDER_SUBSCRIBER_PACKAGE_NAME]);
      return response.records;
    } catch (error) {
      console.log(error);
    }
  }

  private cachedOrgData: IOrgData | null = null; //Cached organization and user data
  
  /**
   * Retrieves the details of the current user and organization with caching.
   *
   * @returns {Promise<IOrgData>} A promise that resolves to an object containing:
   * - User details (username, email, profileName, fullName, etc.)
   * - Organization name and Organization Type
   */
  public async getOrgAndUserDetails(): Promise<IOrgData | null> {
    if (this.cachedOrgData) {
      return this.cachedOrgData;
    }

    try {
        const [userDetails, [orgName, isSandbox]] = await Promise.all([
            this.fetchUserName(),
            this.getCurrentOrgName()
          ]);

      const orgData: IOrgData = {
        user: userDetails,
        orgName: orgName || CQAppConstant.UNKNOWN_ORG,
        isSandbox: isSandbox
      };

      this.cachedOrgData = orgData;

      return orgData;
    } catch (error) {
      console.error(error)
      return null;
    }
  }

  /**
   * Fetches the current user's information from the server.
   *
   * Sends a GET request to the user info endpoint using common headers.
   * 
   * @returns {Promise<User>} A promise that resolves to a `User` object containing the user's details:
   * - `username`: The username of the user.
   * - `email`: The email address of the user.
   * - `photoUrl`: The URL of the user's small profile photo.
   * - `profileName`: The name of the user's profile.
   * - `fullName`: The full name of the user.
   * 
   * @throws Will throw an error if the retrieval or query operation fails.
   */
  async fetchUserName(): Promise<User> {
    try {
      const response = await this.getAxiosInstance().get(CQAppConstant.USER_INFO);
      const user = response.data as User;
      if (user?.orgId) {
        localStorage.setItem(CQAppConstant.LOGGED_IN_ORG_ID, user.orgId);
      }
      return user;
    } catch (error) {
      throw error;
    }
  }
  

  getCurrentOrgId() : string {
    return this.cookie.get(CQAppConstant.REQUEST_HEADERS.X_CURRENT_ORG_ID);
  }

  /**
   * Retrieves the current organization's name and sandbox status.
   *
   * @returns {Promise<[string, boolean]>} A promise that resolves to a tuple containing the organization's name and a boolean indicating if it is a sandbox.
   */
  async getCurrentOrgName(): Promise<[string, boolean]> {
    try {
      const response = await SFConnection.executeQuery(CQAppConstant.ORG_NAME);
      const {Name: orgName, IsSandbox: isSandbox} = response.records[0];
      return [orgName || CQAppConstant.UNKNOWN_ORG, !!isSandbox];
    } catch (error) {
      console.log(error);
      return [CQAppConstant.UNKNOWN_ORG, false];
    }
  }

  async getInstalledPackageVersions() {
    let packageByVersion : {[key:string] : SFPackageVersion} = {};
    let packages = await this.getInstalledPackages();
    packages.forEach(pkg => {
        packageByVersion[pkg.SubscriberPackage.Name] = new SFPackageVersion(pkg.SubscriberPackageVersion.MajorVersion,
            pkg.SubscriberPackageVersion.MinorVersion, pkg.SubscriberPackageVersion.PatchVersion);
    });

    return packageByVersion;
  }


  /**
   * Executes a SOQL query through a backend API.
   *
   * The query is sent to the backend endpoint specified by
   * `CQAppConstant.EXECUTE_QUERY` using an Axios GET request with the query placed in the
   * `X-Query-String` header. The resolved value is `response.data` from the backend.
   *
   * The method logs any caught exceptions to the console and rethrows them to the caller.
   *
   * @param query - The SOQL query or querystring to execute.
   * @returns A Promise that resolves with the query result. Typically an `IQueryResult` for direct jsForce calls; backend responses may vary (`any`).
   * @throws Propagates errors from the backend Axios request or the jsForce `query` callback. Errors are logged before being rethrown.
   */
  async executeQuery(query: string): Promise<IQueryResult | any> {
    try {
        // Execute query through backend API
        const response = await this.getAxiosInstance().get(
          CQAppConstant.EXECUTE_QUERY,
          { headers: { "X-Query-String": query } }
        );
        return response.data;
    } catch (ex) {
      console.error("Error executing query:", ex);
      throw ex;
    }
  }


/**
 * Executes a Salesforce Tooling API query through the backend API.
 * @param query - The SOQL query string to execute against the Tooling API
 * @param replacements - Optional. String or array of strings for query parameter replacements
 * @returns A promise that resolves to the query result object containing records and metadata, or rejects with an error
 * @throws Error if the query execution fails, with the error logged to console
 */
public async executeToolingQuery(query: string, replacements?: string[]): Promise<IQueryResult | any> {
  try {
      // Execute tooling query through backend API
      const headers: any = { "X-Query-String": query };
      if (replacements) headers["x-replacements"] = JSON.stringify(replacements);
      const response = await this.getAxiosInstance().get(
        CQAppConstant.TOOLING_QUERY,
        { headers }
      );
      return response.data;
  } catch (ex) {
    console.error("Error executing tooling query:", ex);
    throw ex;
  }
}

  private cachedProfiles : IProfile[] | null = null;
  async fetchProfiles() : Promise<IProfile[]> {
    if(this.cachedProfiles === null) {
      console.log('Fetching profiles');
      let profiles = await this.executeToolingQuery(CQAppConstant.FETCH_PROFILES);
      let allPromises : any = [];
      this.cachedProfiles= [];
      profiles.records.forEach((profile : any) => {
        let profileDetailPromise = this.executeToolingQuery(CQAppConstant.FETCH_PROFILE_DETAILS, [profile.Id]); 
        allPromises.push(profileDetailPromise.then(
          profileDetail => {
            let detail = profileDetail.records[0];
            this.cachedProfiles?.push(detail);
          }
        ))
      });
      
      await Promise.all(allPromises);
      this.cachedProfiles = this.cachedProfiles.sort((a, b) => a.Name > b.Name ? 1 : a.Name === b.Name ? 0 : -1);
      
      console.log('Fetched profiles');
  }

    return this.cachedProfiles!;
  }

  /**
   * Gets the connection details for the Salesforce instance, including the instance URL, API version, and access token.
   * @returns {Object} - An object containing the connection details.
   */
  getCommonHeaders() {
    return {
      'x-instance-url': this.cookie.get(CQAppConstant.REQUEST_HEADERS.X_INSTANCE_URL),
      'x-version': CQAppConstant.API_VERSION_NUMBER,
      'x-instance-token': this.cookie.get(CQAppConstant.REQUEST_HEADERS.X_INSTANCE_TOKEN),
      'x-current-org-id': this.cookie.get(CQAppConstant.REQUEST_HEADERS.X_CURRENT_ORG_ID)
    };
  }


  /**
   * Creates and returns a configured Axios instance for making HTTP requests.
   * 
   * @returns A new Axios instance configured with:
   *  - baseURL: Set to the proxy URL from getProxyURL()
   *  - headers: Common headers including instance URL, version, and access token
   * 
   * The returned instance can be used to make HTTP requests that automatically include
   * Salesforce authentication and proper base URL configuration.
   */
  public getAxiosInstance(){
    const instance = axios.create({
      baseURL: this.getProxyURL(),
      withCredentials: true
    });

    // Response interceptor to detect session expiry
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check for 401 Unauthorized (session expired)
        const isLogoutRequest = error.config?.url?.includes(CQAppConstant.LOGOUT);
        if (error.response?.status === 401 && !isLogoutRequest) {
          // Trigger session expiry handler via custom event
          window.dispatchEvent(new CustomEvent('session-expired'));
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }

  /**
   * Fetches the Document's Body stored in SF and returns the content
   */
  async fetchDocument(documentId: string) : Promise<string> {
    let proxyUrl = this.getProxyURL() + CQAppConstant.DOCUMENT_SERVICE;
    let headers = {
      ...this.getCommonHeaders(),
      accept: 'application/x-zip-compressed',
      'x-original-id': documentId,
    };

    let response = await fetch(`${proxyUrl}`, {
        method: 'GET',
        headers: headers,
        credentials: 'include'
    }).then((res) => {
      return res.text();
    });

    return response;
  }

  /**
  * Returns the base URL of the Salesforce connector.
  * @returns The base URL as a string.
  */
    public getBaseURL() : string {
      return this.instanceUrl;
    }

  /**
   * Queries Salesforce for the Migration Logs Report by name and returns
   * the full URL to view it. Falls back to deployment status path if not found.
   */
  async getMigrationLogsReportUrl(): Promise<string> {
    const baseUrl = this.getBaseURL();
    try {
      const result = await this.executeQuery(CQAppConstant.QUERIES.migrationLogsReport);
      const reportId = result?.records?.[0]?.Id;
      const path = reportId
        ? CQAppConstant.MIGRATION_REPORT_VIEW_PATH.replace('{id}', reportId)
        : CQAppConstant.DEPLOYMENT_STATUS_PATH;
      return baseUrl + path;
    } catch (ex) {
      console.error('Error fetching migration logs report:', ex);
      return baseUrl + CQAppConstant.DEPLOYMENT_STATUS_PATH;
    }
  }
}

const SFConnection = new SfConnector();

export { SFConnection };